<template>
  <v-container id="form">
    <div v-if="isLoggedIn" class="photo-documentation mb-8">
      <h3 class="mb-4">Fotodokumentace</h3>
      <div class="d-flex flex-column flex-sm-row">
        <v-btn
          v-if="isCordova"
          :loading="imageLoading"
          color="#324c8d"
          class="add-photo-btn mr-sm-2 mb-4 mb-sm-0"
          dark
          depressed
          @click="handleAppPhoto"
        >
          <v-icon left> mdi-camera </v-icon> {{ $t("take_photo") }}
        </v-btn>
        <v-btn
          :loading="imageLoading"
          type="file"
          color="#324c8d"
          class="add-photo-btn mb-2"
          dark
          depressed
          @click="chooseFile"
        >
          <v-icon left> mdi-paperclip </v-icon>
          {{ $t("add_photo") }}
        </v-btn>
        <input
          ref="file-upload-input"
          id="file-upload-input"
          type="file"
          accept="image/*"
          hidden
          @change="uploadFile($event.target.files[0])"
        />
      </div>
      <v-chip-group
        v-if="rating.images.length"
        mandatory
        active-class="primary--text"
      >
        <v-chip
          v-for="image in rating.images"
          :key="image.id"
          close
          @click="showImageDetail(image)"
          @click:close="removeImage(image)"
        >
          <div class="chip-text">
            {{ image.name || image.description || image.id }}
          </div>
        </v-chip>
      </v-chip-group>
    </div>
    <h3>{{ $t("assessment_eczema_region") }}</h3>
    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          :rules="RangeRules"
          class="inputs headEczemaRangePercentage"
          :label="$t('rating_range')"
          outlined
          dense
          maxlength="3"
          v-model.number="rating.range"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          class="inputs headEczemaRangeScore"
          :label="$t('range_score')"
          dense
          readonly
          v-model.number="rating.rangeScore"
        ></v-text-field>
      </v-col>
    </v-row>
    <h3>{{ $t("assessment_severity") }}</h3>
    <v-row>
      <v-col cols="12" md="6">
        <label>{{ $t("degree_severity_erytem") }}</label>
        <v-slider
          v-model.number="rating.erytemSeverity"
          class="align-center mySlider"
          :max="max"
          :min="min"
          hide-details
          step="0.5"
        >
          <template v-slot:append>
            <v-text-field
              v-model.number="rating.erytemSeverity"
              class="mt-0 pt-0"
              dense
              outlined
              hide-details
              type="number"
              style="width: 60px"
            ></v-text-field>
          </template>
        </v-slider>
      </v-col>
      <v-col cols="12" md="6" class="scoreInputs">
        <v-select
          class="inputs headerytemStage"
          :label="$t('erytem_severity')"
          :items="items"
          dense
          v-model="rating.erytemSeverityScore"
        ></v-select>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="6">
        <label>{{ $t("degree_severity_papules") }}</label>
        <v-slider
          v-model.number="rating.papulesSeverity"
          class="align-center mySlider"
          :max="max"
          :min="min"
          hide-details
          step="0.5"
        >
          <template v-slot:append>
            <v-text-field
              v-model.number="rating.papulesSeverity"
              class="mt-0 pt-0"
              dense
              outlined
              hide-details
              single-line
              type="number"
              style="width: 60px"
            ></v-text-field>
          </template>
        </v-slider>
      </v-col>
      <v-col cols="12" md="6" class="scoreInputs">
        <v-select
          class="inputs headpapulePlakyStage"
          :label="$t('papules_severity')"
          :items="items"
          dense
          v-model="rating.papulesSeverityScore"
        ></v-select>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="6">
        <label>{{ $t("degree_severity_excoriation") }}</label>
        <v-slider
          v-model.number="rating.exorcationsSeverity"
          class="align-center mySlider"
          :max="max"
          :min="min"
          hide-details
          step="0.5"
        >
          <template v-slot:append>
            <v-text-field
              v-model.number="rating.exorcationsSeverity"
              class="mt-0 pt-0"
              dense
              outlined
              hide-details
              single-line
              type="number"
              style="width: 60px"
            ></v-text-field>
          </template>
        </v-slider>
      </v-col>
      <v-col cols="12" md="6" class="scoreInputs">
        <v-select
          class="inputs headexkoriaceStage"
          :label="$t('excoriation_severity')"
          :items="items"
          dense
          v-model="rating.exorcationsSeverityScore"
        ></v-select>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="6">
        <label>{{ $t("degree_severity_lichenification") }}</label>
        <v-slider
          v-model.number="rating.lichenificationSeverity"
          class="align-center mySlider"
          :max="max"
          :min="min"
          hide-details
          step="0.5"
        >
          <template v-slot:append>
            <v-text-field
              v-model.number="rating.lichenificationSeverity"
              class="mt-0 pt-0"
              dense
              outlined
              hide-details
              single-line
              type="number"
              style="width: 60px"
            ></v-text-field>
          </template>
        </v-slider>
      </v-col>
      <v-col cols="12" md="6" class="scoreInputs">
        <v-select
          class="inputs lichenifikaceStage"
          :label="$t('lichenification_severity')"
          dense
          :items="items"
          v-model="rating.lichenificationSeverityScore"
        ></v-select>
      </v-col>
    </v-row>

    <UploadImageDialog
      :showDialog="showDialog"
      :imageDetail="imageDetail"
      @imageDescription="getImageDescription"
      @uploadImage="uploadImage"
      @close-dialog="showDialog = false"
    />
  </v-container>
</template>

<script>
import tokenMixin from "@/mixins/tokenMixin.js";
import UploadImageDialog from "@/components/UploadImageDialog.vue";

export default {
  components: { UploadImageDialog },
  props: ["rating"],
  mixins: [tokenMixin],
  data() {
    return {
      min: 0.5,
      max: 3,
      showDialog: false,
      imageDetail: {},
      imageLoading: false
    };
  },

  computed: {
    isLoggedIn() {
      return this.$store.getters.isUserAuthenticated;
    },
    isCordova() {
      return this.$store.getters.isCordova;
    },
    RangeRules() {
      return [value => !!value || this.$t("please_fill_eczema")];
    },
    items() {
      return [this.$t("slightly_heavy"), this.$t("medium"), this.$t("severe")];
    }
  },

  methods: {
    getFileEntry(fileURL) {
      return new Promise((resolve, reject) => {
        window.resolveLocalFileSystemURL(
          fileURL,
          fileEntry => {
            resolve(fileEntry);
          },
          err => {
            reject(err);
          }
        );
      });
    },
    readFile(fileEntry) {
      return new Promise((resolve, reject) => {
        fileEntry.file(
          file => {
            let reader = new FileReader();
            reader.readAsArrayBuffer(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
          },
          err => {
            reject(err);
          }
        );
      });
    },
    takePhoto() {
      return new Promise((resolve, reject) => {
        navigator.camera.getPicture(
          imagePath => {
            resolve(imagePath);
          },
          err => {
            reject(err);
          },
          {
            quality: 75,
            allowEdit: true,
            correctOrientation: true
          }
        );
      });
    },
    async handleAppPhoto() {
      try {
        const imagePath = await this.takePhoto();
        const fileEntry = await this.getFileEntry(imagePath);
        const file = await this.readFile(fileEntry);
        await this.uploadFile(
          new Blob([file], { type: "image/jpeg" }),
          "image.jpg"
        );
      } catch (error) {
        console.error(error);
      }
    },
    chooseFile() {
      const fileInput = this.$refs["file-upload-input"];
      fileInput && fileInput.click();
    },
    showImageInNewTab(imgUrl) {
      open(imgUrl);
    },
    getImageDescription(value) {
      this.imageDetail.description = value;
    },
    uploadImage() {
      this.showDialog = false;
      this.$emit("addImage", this.imageDetail);
    },
    removeImage({ id }) {
      this.$emit("removeImage", id);
    },
    showImageDetail(image) {
      this.showDialog = true;
      this.imageDetail = image;
    },
    async uploadFile(file, name = "") {
      this.imageLoading = true;
      await this.$_checkTokenExpiration();
      let formData = new FormData();
      if (name) {
        formData.append("files", file, name);
      } else {
        formData.append("files", file);
      }
      try {
        const {
          body: { files: filesArray }
        } = await this.$http.post("files/images", formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        });

        this.imageDetail = { ...filesArray[0], name: file.name };

        this.showDialog = true;
      } catch (error) {
        console.log(error);
      }
      this.imageLoading = false;
    }
  },

  watch: {
    "rating.range": function(val) {
      this.rating.range = val;
      if (val == 0) {
        this.rating.rangeScore = 0;
      }
      if (val >= 1 && val <= 9) {
        this.rating.rangeScore = 1;
      }
      if (val > 9 && val <= 29) {
        this.rating.rangeScore = 2;
      }
      if (val > 29 && val <= 49) {
        this.rating.rangeScore = 3;
      }
      if (val > 49 && val <= 69) {
        this.rating.rangeScore = 4;
      }
      if (val > 69 && val <= 89) {
        this.rating.rangeScore = 5;
      }
      if (val > 89 && val <= 100) {
        this.rating.rangeScore = 6;
      }
      if (val > 100) {
        this.rating.range = 100;
      }
    },
    //Eryten
    "rating.erytemSeverity": function(val) {
      this.rating.erytemSeverity = val;

      if (val == 0.5) {
        this.rating.erytemSeverity = 0;
      }
      if (val <= 0.5) {
        this.rating.erytemSeverityScore = "";
      }
      if (val == 1) {
        this.rating.erytemSeverityScore = this.items[0];
      }
      if (val > 1) {
        this.rating.erytemSeverityScore = this.items[1];
      }
      if (val > 2) {
        this.rating.erytemSeverityScore = this.items[2];
      }
    },
    "rating.erytemSeverityScore": function(val) {
      this.rating.erytemSeverityScore = val;
      if (val == this.items[0]) {
        this.rating.erytemSeverity = 1;
      }
      if (val == this.items[1]) {
        this.rating.erytemSeverity = 2;
      }
      if (val == this.items[2]) {
        this.rating.erytemSeverity = 3;
      }
    },
    // Papuly plaky
    "rating.papulesSeverity": function(val) {
      this.rating.papulesSeverity = val;
      if (val == 0.5) {
        this.rating.papulesSeverity = 0;
      }
      if (val <= 0.5) {
        this.rating.papulesSeverityScore = "";
      }
      if (val == 1) {
        this.rating.papulesSeverityScore = this.items[0];
      }
      if (val > 1) {
        this.rating.papulesSeverityScore = this.items[1];
      }
      if (val > 2) {
        this.rating.papulesSeverityScore = this.items[2];
      }
    },
    "rating.papulesSeverityScore": function(val) {
      this.rating.papulesSeverityScore = val;
      if (val == this.items[0]) {
        this.rating.papulesSeverity = 1;
      }
      if (val == this.items[1]) {
        this.rating.papulesSeverity = 2;
      }
      if (val == this.items[2]) {
        this.rating.papulesSeverity = 3;
      }
    },
    //Exkoriace
    "rating.exorcationsSeverity": function(val) {
      this.rating.exorcationsSeverity = val;
      if (val == 0.5) {
        this.rating.exorcationsSeverity = 0;
      }
      if (val <= 0.5) {
        this.rating.exorcationsSeverityScore = "";
      }
      if (val == 1) {
        this.rating.exorcationsSeverityScore = this.items[0];
      }
      if (val > 1) {
        this.rating.exorcationsSeverityScore = this.items[1];
      }
      if (val > 2) {
        this.rating.exorcationsSeverityScore = this.items[2];
      }
    },
    "rating.exorcationsSeverityScore": function(val) {
      this.rating.exorcationsSeverityScore = val;
      if (val == this.items[0]) {
        this.rating.exorcationsSeverity = 1;
      }
      if (val == this.items[1]) {
        this.rating.exorcationsSeverity = 2;
      }
      if (val == this.items[2]) {
        this.rating.exorcationsSeverity = 3;
      }
    },
    //Lichenifikace
    "rating.lichenificationSeverity": function(val) {
      this.rating.lichenificationSeverity = val;
      if (val == 0.5) {
        this.rating.lichenificationSeverity = 0;
      }
      if (val <= 0.5) {
        this.rating.lichenificationSeverityScore = "";
      }
      if (val == 1) {
        this.rating.lichenificationSeverityScore = this.items[0];
      }
      if (val > 1) {
        this.rating.lichenificationSeverityScore = this.items[1];
      }
      if (val > 2) {
        this.rating.lichenificationSeverityScore = this.items[2];
      }
    },
    "rating.lichenificationSeverityScore": function(val) {
      this.rating.lichenificationSeverityScore = val;
      if (val == this.items[0]) {
        this.rating.lichenificationSeverity = 1;
      }
      if (val == this.items[1]) {
        this.rating.lichenificationSeverity = 2;
      }
      if (val == this.items[2]) {
        this.rating.lichenificationSeverity = 3;
      }
    }
  }
};
</script>
<style lang="scss">
.chip-text {
  max-width: 8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
<style scoped lang="scss">
#form {
  ::v-deep.v-slider__thumb {
    cursor: pointer;
  }
}

.image-name:hover {
  text-decoration: underline;
}

.scoreInputs {
  padding-top: 2.7em;
}
::v-deep.v-text-field {
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type="number"] {
    -moz-appearance: textfield; /* Firefox */
  }
}

@media only screen and (min-width: 200px) and (max-width: 425px) {
  ::v-deep.mySlider > div:first-of-type {
    display: none;
  }

  ::v-deep .v-input__append-outer {
    margin-left: 0;
  }

  .scoreInputs {
    padding-top: 1rem;
  }
}
</style>
